import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { ProductComments } from '@/components/ProductComments';
import { 
  ShoppingCart, 
  TrendingDown, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Target, 
  Plus,
  Calendar,
  Euro,
  Package,
  BarChart3,
  AlertCircle,
  Link,
  LogOut,
  Search,
  User,
  MoreVertical,
  TrendingUp,
  Sparkles,
  Settings,
  Filter,
  BookOpen,
  Building2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase, withRetry } from '@/lib/supabase';
import { LogoutButton } from '@/components/LogoutButton';
import { AddProductDialog } from '@/components/AddProductDialog';
import { toast } from 'sonner';
import { ProductDetails } from '@/components/ProductDetails';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ProductEditMenu } from '@/components/ProductEditMenu';

import confetti from 'canvas-confetti';
import { ProfileModal } from '@/components/ProfileModal';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { SUBSCRIPTION_LIMITS } from '@/types/subscription';
import { SavingsGoalCalculator } from '@/components/SavingsGoalCalculator';
import { UpgradePrompt } from '@/components/UpgradePrompt';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ImpulseControl } from '@/components/ImpulseControl';
import { TutorialVideo } from '@/components/TutorialVideo';
import { WelcomeOverlay } from '@/components/WelcomeOverlay';
import { TutorialIconButton } from '@/components/TutorialIconButton';
import { FeedbackIconButton } from '@/components/FeedbackIconButton';
import { WelcomeAnimation } from '@/components/WelcomeAnimation';
import { SubscriptionOverview } from '@/components/SubscriptionOverview';


interface WaitlistItem {
  id: string;
  name: string;
  price: number;
  category: string;
  created_at: string;
  url?: string;
  status: 'waiting' | 'bought' | 'skipped';
  decision_date?: string;
  originalPrice: number;
}

interface Profile {
  id: string;
  full_name: string;
  email: string;
  custom_full_name?: boolean;
}

function getGreetingEmoji() {
  const hour = new Date().getHours();
  if (hour < 12) return '🌅';
  if (hour < 18) return '☀️';
  return '🌙';
}

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { subscription, showUpgradeModal } = useSubscription();
  const [waitlistItems, setWaitlistItems] = useState<WaitlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [newItemUrl, setNewItemUrl] = useState('');
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<WaitlistItem | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'waiting' | 'bought' | 'skipped' | 'subscriptions'>('waiting');
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [animatingItemId, setAnimatingItemId] = useState<string | null>(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [hasShownWelcomeToast, setHasShownWelcomeToast] = useState(false);
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(false);

  useEffect(() => {
    if (user) {
      fetchWaitlistItems();
      fetchUserProfile();
      
      // Intelligentes Upgrade-Prompt System für Free-User
      if (subscription?.tier === 'free' || !subscription) {
        const lastShown = localStorage.getItem('upgradePromptLastShown');
        const lastDismissed = localStorage.getItem('upgradePromptLastDismissed');
        const snoozeUntil = localStorage.getItem('upgradePromptSnoozeUntil');
        const now = Date.now();
        
        // Prüfe ob Snooze noch aktiv ist
        const isSnoozing = snoozeUntil && now < parseInt(snoozeUntil);
        
        // Zeige Prompt nur wenn:
        // 1. Nicht im Snooze-Modus UND
        // 2. (Noch nie gezeigt wurde ODER
        //    Letztes Mal vor mehr als 2 Stunden gezeigt wurde) UND
        // 3. Nicht in den letzten 30 Minuten geschlossen wurde
        const shouldShow = !isSnoozing && 
          (!lastShown || now - parseInt(lastShown) > 2 * 60 * 60 * 1000) && // 2 Stunden
          (!lastDismissed || now - parseInt(lastDismissed) > 30 * 60 * 1000); // 30 Minuten
        
        if (shouldShow) {
        const timer = setTimeout(() => {
          setShowUpgradePrompt(true);
            localStorage.setItem('upgradePromptLastShown', now.toString());
          }, 5000); // Show after 5 seconds (weniger aggressiv)
        
        return () => clearTimeout(timer);
        }
      }
    }
  }, [user, subscription]);

  // Willkommens-Toast anzeigen
  useEffect(() => {
    if (user && profile && !hasShownWelcomeToast) {
      const greeting = getGreetingEmoji();
      // Priorität: 1. Profil-Name (falls custom), 2. Profil-Name (allgemein), 3. Google-Name, 4. E-Mail-Fallback
      const name = (profile.full_name && profile.custom_full_name) ? 
                   profile.full_name.split(' ')[0] :
                   profile.full_name ? 
                   profile.full_name.split(' ')[0] :
                   user.user_metadata?.full_name?.split(' ')[0] || 
                   user.user_metadata?.name?.split(' ')[0] || 
                   user.email?.split('@')[0] || 
                   'Kunde';
      
      // Zeige elegante Willkommens-Animation
      setShowWelcomeAnimation(true);
      
      setHasShownWelcomeToast(true);
    }
  }, [user, profile, hasShownWelcomeToast]);

  // Onboarding-Produkt hinzufügen - NACH dem Laden der Warteliste
  useEffect(() => {
    if (user && !loading && waitlistItems.length >= 0) {
      const onboardingProduct = localStorage.getItem('onboarding_product');
      if (onboardingProduct) {
        try {
          const productData = JSON.parse(onboardingProduct);
          // Lösche sofort aus localStorage um doppelte Verarbeitung zu verhindern
          localStorage.removeItem('onboarding_product');
          // Füge das Produkt zur Warteliste hinzu (mit Limit-Check)
          addOnboardingProduct(productData);
        } catch (error) {
          console.error('Fehler beim Verarbeiten des Onboarding-Produkts:', error);
          localStorage.removeItem('onboarding_product');
        }
      }
    }
  }, [user, loading, waitlistItems.length]);

  // Auto-Refresh wenn Laden zu lange dauert - erhöhtes Timeout
  useEffect(() => {
    if (loading) {
      const refreshTimer = setTimeout(() => {
        console.log('Dashboard loading timeout - refreshing page');
        window.location.reload();
      }, 12000); // Refresh nach 12 Sekunden (erhöht für bessere Stabilität)

      return () => clearTimeout(refreshTimer);
    }
  }, [loading]);

  const fetchUserProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user?.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }
      setProfile(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchWaitlistItems = async () => {
    if (!user) return;
    try {
      // Verwende withRetry für robuste API-Aufrufe
      const data = await withRetry(async () => {
        const { data, error } = await supabase
          .from('waitlist')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;
        return data;
      });
      
      // Für jedes Produkt den aktuellsten Preis aus price_history holen
      const itemsWithLatestPrice = await Promise.all(
        (data || []).map(async (item) => {
          try {
            const priceHistory = await withRetry(async () => {
              const { data: priceData, error } = await supabase
                .from('price_history')
                .select('price')
                .eq('product_id', item.id)
                .eq('user_id', user.id)
                .order('tracked_at', { ascending: false })
                .limit(1);
              
              if (error) throw error;
              return priceData;
            });
            
            // Verwende den neuesten Preis aus der Historie oder den ursprünglichen Preis
            const latestPrice = priceHistory && priceHistory.length > 0 
              ? priceHistory[0].price 
              : item.price;
            
            return {
              ...item,
              price: latestPrice,
              originalPrice: item.price // Behalte den ursprünglichen Preis für Referenz
            };
          } catch (priceError) {
            console.warn('Fehler beim Laden der Preishistorie für Produkt:', item.id, priceError);
            // Fallback auf ursprünglichen Preis
            return {
              ...item,
              originalPrice: item.price
            };
          }
        })
      );
      
      setWaitlistItems(itemsWithLatestPrice);
    } catch (error) {
      console.error('Error fetching waitlist items:', error);
      toast.error('Fehler beim Laden der Produkte. Bitte aktualisieren Sie die Seite.');
    } finally {
      setLoading(false);
    }
  };

  // Optimierte Funktion für sofortige lokale Updates nach Produktbearbeitung
  const updateLocalProduct = async (productId: string, updates: Partial<WaitlistItem>) => {
    setWaitlistItems(prevItems => 
      prevItems.map(item => 
        item.id === productId 
          ? { ...item, ...updates }
          : item
      )
    );
  };

  // Funktion zum Hinzufügen eines Onboarding-Produkts mit Limit-Check
  const addOnboardingProduct = async (productData: {
    name: string;
    price: number;
    category: string;
    url?: string;
  }) => {
    if (!user) return;

    try {
      // Prüfe aktuelle Wartelisten-Anzahl
      const currentWaitingCount = waitlistItems.filter(item => item.status === 'waiting').length;
      const currentTier = subscription?.tier || 'free';
      const maxProducts = SUBSCRIPTION_LIMITS[currentTier].maxActiveProducts;

      // Wenn Limit erreicht, zeige Upgrade-Modal und lösche localStorage
      if (currentWaitingCount >= maxProducts) {
        localStorage.removeItem('onboarding_product');
        
        toast.error(`Limit erreicht! Als ${currentTier === 'free' ? 'Free-User' : 'Nutzer'} kannst du maximal ${maxProducts} Produkt${maxProducts > 1 ? 'e' : ''} auf der Warteliste haben.`, {
          duration: 8000,
          action: {
            label: 'Jetzt upgraden',
            onClick: () => showUpgradeModal()
          }
        });
        
        // Zeige Upgrade-Modal
        showUpgradeModal();
        return;
      }

      const { error } = await supabase
        .from('waitlist')
        .insert([
          {
            user_id: user.id,
            name: productData.name,
            price: productData.price,
            category: productData.category,
            url: productData.url || null
          }
        ]);

      if (error) throw error;

      // Aktualisiere die Warteliste
      await fetchWaitlistItems();
      
      toast.success(`"${productData.name}" wurde erfolgreich zur Warteliste hinzugefügt!`, {
        duration: 5000
      });
    } catch (error) {
      console.error('Fehler beim Hinzufügen des Onboarding-Produkts:', error);
      toast.error('Das Produkt konnte nicht hinzugefügt werden.');
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getDaysLeft = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return 30 - diffDays;
  };

  const handleAddItem = async () => {
    setIsAddingItem(true);
    try {
      // Hier würde normalerweise die Link-Import Logik kommen
      const { data, error } = await supabase
        .from('waitlist')
        .insert([
          {
            user_id: user?.id,
            name: 'Neues Produkt',
            price: 0,
            category: 'Sonstiges',
            url: newItemUrl
          }
        ])
        .select();

      if (error) throw error;
      
      setWaitlistItems([...(data || []), ...waitlistItems]);
      setNewItemUrl('');
    } catch (error) {
      console.error('Error adding item:', error);
    } finally {
      setIsAddingItem(false);
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Business': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Nice-to-Have': return 'bg-pink-100 text-pink-800 border-pink-200';
      case 'Persönlich': return 'bg-green-100 text-green-800 border-green-200';
      case 'Sonstiges': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCategoryInfo = (category: string) => {
    switch (category) {
      case 'Business': 
        return { 
          name: 'Business', 
          description: 'Berufliche Anschaffungen und Investitionen',
          icon: '💼'
        };
      case 'Nice-to-Have': 
        return { 
          name: 'Nice-to-Have', 
          description: 'Wünschenswerte aber nicht notwendige Artikel',
          icon: '🎯'
        };
      case 'Persönlich': 
        return { 
          name: 'Persönlich', 
          description: 'Private Anschaffungen und Hobbys',
          icon: '🏠'
        };
      case 'Sonstiges': 
        return { 
          name: 'Sonstiges', 
          description: 'Andere Kategorien',
          icon: '📦'
        };
      default: 
        return { 
          name: category, 
          description: 'Unbekannte Kategorie',
          icon: '❓'
        };
    }
  };

  const handleStatusChange = async (itemId: string, newStatus: 'waiting' | 'bought' | 'skipped') => {
    if (!user) return;
    
    // Überprüfe Limit beim Zurücksetzen auf 'waiting'
    if (newStatus === 'waiting') {
      const currentWaitingCount = waitlistItems.filter(item => item.status === 'waiting').length;
      const currentTier = subscription?.tier || 'free';
      const maxProducts = SUBSCRIPTION_LIMITS[currentTier].maxActiveProducts;
      
      if (currentWaitingCount >= maxProducts) {
        toast.error(`Limit erreicht! Du kannst maximal ${maxProducts} Produkt${maxProducts > 1 ? 'e' : ''} in der Warteliste haben.`);
        return;
      }
    }
    
    try {
      setAnimatingItemId(itemId);
      
      // Verwende withRetry für robuste API-Aufrufe
      await withRetry(async () => {
        const { error } = await supabase
          .from('waitlist')
          .update({
            status: newStatus,
            decision_date: newStatus === 'waiting' ? null : new Date().toISOString(),
            user_id: user.id
          })
          .eq('id', itemId)
          .eq('user_id', user.id);

        if (error) throw error;
        return true;
      });

      // Aktualisiere die lokale Liste
      updateLocalProduct(itemId, { status: newStatus, decision_date: newStatus === 'waiting' ? null : new Date().toISOString() });

      // Spezielle Animationen für verschiedene Status
      if (newStatus === 'skipped') {
        // Langsamere und reichhaltigere Konfetti-Animation
        const duration = 3000;
        const animationEnd = Date.now() + duration;
        const colors = ['#10B981', '#059669', '#34D399'];

        const frame = () => {
          confetti({
            particleCount: 2,
            angle: 60,
            spread: 55,
            origin: { x: 0, y: 0.8 },
            colors: colors,
          });
          confetti({
            particleCount: 2,
            angle: 120,
            spread: 55,
            origin: { x: 1, y: 0.8 },
            colors: colors,
          });

          if (Date.now() < animationEnd) {
            requestAnimationFrame(frame);
          }
        };
        frame();
      }

      // Zeige Erfolgsmeldung
      toast.success(
        newStatus === 'waiting'
          ? 'Status zurückgesetzt'
          : newStatus === 'bought'
            ? '🎉 Viel Freude mit deinem neuen Produkt!'
            : '💰 Super! Du hast dich gegen einen Impulskauf entschieden'
      );

      // Warte auf Animation und wechsle dann den Tab
      setTimeout(() => {
        setAnimatingItemId(null);
        if (newStatus !== 'waiting') {
          setActiveTab(newStatus);
        }
      }, 500);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Status konnte nicht aktualisiert werden. Bitte versuchen Sie es erneut.');
      setAnimatingItemId(null);
    }
  };

  const filteredItems = waitlistItems
    .filter(item => {
      const matchesSearch = searchTerm ? (
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase())
      ) : true;
      
      const matchesStatus = activeTab === 'all' || item.status === activeTab;
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      
      return matchesSearch && matchesStatus && matchesCategory;
    })
    .sort((a, b) => {
      // Nach Erstellungsdatum sortieren (neuere zuerst)
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  const getStatusCounts = () => {
    return waitlistItems.reduce((acc, item) => ({
      waiting: acc.waiting + (item.status === 'waiting' ? 1 : 0),
      bought: acc.bought + (item.status === 'bought' ? 1 : 0),
      skipped: acc.skipped + (item.status === 'skipped' ? 1 : 0)
    }), { waiting: 0, bought: 0, skipped: 0 });
  };

  const calculateTotal = (status: 'bought' | 'skipped') => {
    return waitlistItems
      .filter(item => item.status === status)
      .reduce((sum, item) => sum + item.price, 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="mb-6">
            <div className="p-3 bg-emerald-100 rounded-full inline-flex items-center justify-center mb-4">
              <Clock className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Dashboard wird geladen...</h3>
            <p className="text-gray-600 text-sm mb-6">Deine Sparziele werden vorbereitet</p>
          </div>
          
          {/* Progress Bar im Stil der Tage-verbleibend */}
          <div className="w-full max-w-xs mx-auto">
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              <span>Start</span>
              <span>Fertig</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="h-3 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 transition-all duration-1000 animate-pulse"
                style={{ width: '75%' }}
              />
            </div>
            <div className="mt-2 text-sm text-emerald-600 font-medium">
              Lädt...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-md border-b border-gray-100 z-50">
        <div className="container mx-auto px-4 py-3 flex items-center min-h-[64px]">
          {/* Logo - Desktop: mit Text, Mobile: nur Icon */}
          <div className="flex items-center gap-2">
            <a 
              href="/" 
              onClick={(e) => { 
                e.preventDefault(); 
                navigate('/');
              }} 
              className="flex items-center gap-2 hover:scale-105 transition-all duration-300 cursor-pointer group"
            >
              <img 
                src="/Favicon/favicon-196x196.png" 
                alt="KaufPause Logo" 
                className="w-7 h-7 sm:w-8 sm:h-8 -translate-y-0.5"
              />
              {/* Text nur auf Desktop */}
              <span className="hidden sm:block text-lg sm:text-xl font-bold text-gray-900">
                Kauf<span className="bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-700 bg-clip-text text-transparent group-hover:from-emerald-400 group-hover:via-emerald-500 group-hover:to-emerald-600 transition-all duration-300">Pause</span>
              </span>
            </a>
          </div>

          {/* Begrüßung - zentriert */}
          <div className="flex-1 flex justify-center items-center text-gray-600">
            {/* Mobile: Vollständige Begrüßung */}
            <span className="text-sm font-medium lg:hidden text-center">
              {getGreetingEmoji()} Hallo, {profile?.full_name || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'Nutzer'}!
            </span>
            {/* Desktop: Vollständige Begrüßung */}
            <span className="text-base font-medium hidden lg:block text-center">
              {getGreetingEmoji()} Hallo, {profile?.full_name || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || 'Nutzer'}!
            </span>
          </div>

          {/* Aktionen */}
          <div className="flex items-center gap-1 sm:gap-2">

            
            {/* Upgrade Button - nur auf Desktop */}
            {((subscription?.tier === 'trial' && subscription?.trial_end) || subscription?.tier === 'free') && (
              <Button
                onClick={() => {
                  showUpgradeModal();
                }}
                size="sm"
                className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white hidden lg:flex px-3 py-1.5 text-sm"
              >
                <Sparkles className="w-3 h-3 mr-1.5" />
                Upgraden
              </Button>
            )}
            
            {/* Kompakte Buttons */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsProfileOpen(true)}
              className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50"
              title="Profil bearbeiten"
            >
              <User className="w-4 h-4" />
              <span className="hidden sm:inline text-sm">Profil</span>
            </Button>
            
            <TutorialIconButton />
            
            <div className="hidden md:block">
              <FeedbackIconButton />
            </div>
            
            <LogoutButton />
          </div>
        </div>
      </nav>

      {/* Elegante Willkommens-Animation */}
      <WelcomeAnimation
        show={showWelcomeAnimation}
        userName={
          (profile?.full_name && profile?.custom_full_name) ? 
          profile.full_name.split(' ')[0] :
          profile?.full_name ? 
          profile.full_name.split(' ')[0] :
          user?.user_metadata?.full_name?.split(' ')[0] || 
          user?.user_metadata?.name?.split(' ')[0] || 
          user?.email?.split('@')[0] || 
          'Nutzer'
        }
        greeting={getGreetingEmoji()}
        onComplete={() => setShowWelcomeAnimation(false)}
      />

      <main className="container mx-auto px-4 py-4 pt-20">
        {/* Beta-Hinweis */}
        <div className="mb-6 p-4 bg-white/40 backdrop-blur-sm border border-gray-100/50 rounded-xl">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 mb-1">Beta-Version</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Du nutzt die Beta-Version von KaufPause. Dein Feedback hilft uns dabei, die App noch besser zu machen!
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-4 mb-8">
          <Card 
            className="group bg-white border border-gray-200/60 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300"
            onClick={() => setActiveTab('waiting')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 font-medium">In Warteliste</p>
                  <p className="text-3xl font-light text-gray-900">{getStatusCounts().waiting}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-300">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="group bg-white border border-gray-200/60 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300"
            onClick={() => setActiveTab('bought')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 font-medium">Gekauft</p>
                  <p className="text-3xl font-light text-gray-900">{getStatusCounts().bought}</p>
                  <p className="text-xs text-gray-500">{Math.round(calculateTotal('bought'))}€ ausgegeben</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors duration-300">
                  <CheckCircle className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="group bg-white border border-gray-200/60 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:border-gray-300"
            onClick={() => setActiveTab('skipped')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-gray-600 font-medium">Impulskauf vermieden</p>
                  <p className="text-3xl font-light text-gray-900">{getStatusCounts().skipped}</p>
                  <p className="text-xs text-emerald-600 font-medium">{Math.round(calculateTotal('skipped'))}€ gespart</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors duration-300">
                  <XCircle className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="group bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200/60 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-300">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm text-emerald-700 font-medium">Gesamt Ersparnis</p>
                  <p className="text-3xl font-light text-emerald-900">{Math.round(calculateTotal('skipped'))}€</p>
                  <p className="text-xs text-emerald-600">Dein Erfolg</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors duration-300">
                  <TrendingUp className="w-6 h-6 text-emerald-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sparziel-Rechner - Minimalistisch über der Warteliste */}
        <SavingsGoalCalculator 
          currentSavings={calculateTotal('skipped')}
          averageSkippedPerMonth={(() => {
            const skippedItems = waitlistItems.filter(item => item.status === 'skipped');
            if (skippedItems.length === 0) return 0;
            
            // Berechne Monate seit Registrierung
            const registrationDate = new Date(user?.created_at || Date.now());
            const monthsSinceRegistration = Math.max(1, Math.ceil((Date.now() - registrationDate.getTime()) / (1000 * 60 * 60 * 24 * 30)));
            
            return Math.round(skippedItems.length / monthsSinceRegistration);
          })()}
        />

        {/* Widgets für zusätzliche Funktionen */}
        <div className="grid gap-6 md:grid-cols-3 mb-8">
          {/* Platz für weitere Widgets */}
        </div>

        <Card className="bg-white border border-gray-200/60 shadow-sm hover:shadow-md transition-all duration-300">
          <CardHeader className="pb-4">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              <div>
                <CardTitle className="text-xl font-light text-gray-900">
                  {activeTab === 'subscriptions' ? 'Abo-Verwaltung' : 'Aktuelle Warteliste'}
                </CardTitle>
                <CardDescription className="text-gray-600 mt-1">
                  {activeTab === 'subscriptions' 
                    ? 'Verwalten Sie Ihre Abonnements und behalten Sie die Kosten im Blick'
                    : 'Produkte in der 30-Tage Wartezeit'
                  }
                </CardDescription>
              </div>
              {activeTab !== 'subscriptions' && (
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1 sm:flex-none">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-600" />
                    <Input
                      placeholder="Produkt oder Kategorie suchen..."
                      className="pl-10 w-full sm:w-64 bg-white border-gray-200 focus:bg-white focus:border-emerald-300 focus:ring-emerald-200/50 transition-all duration-200 rounded-xl"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`h-10 w-10 p-0 bg-white border-gray-200 hover:bg-gray-50 hover:border-emerald-300 transition-all duration-200 rounded-xl ${
                        categoryFilter !== 'all' ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : ''
                      }`}
                      title="Kategorie filtern"
                    >
                      <Filter className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-white border-gray-200 rounded-xl shadow-lg">
                    <DropdownMenuItem 
                      onClick={() => setCategoryFilter('all')}
                      className={`rounded-lg ${categoryFilter === 'all' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span>🔍</span>
                        <span>Alle Kategorien</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setCategoryFilter('Business')}
                      className={`rounded-lg ${categoryFilter === 'Business' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span>💼</span>
                        <span>Business</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setCategoryFilter('Nice-to-Have')}
                      className={`rounded-lg ${categoryFilter === 'Nice-to-Have' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span>🎯</span>
                        <span>Nice-to-Have</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setCategoryFilter('Persönlich')}
                      className={`rounded-lg ${categoryFilter === 'Persönlich' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span>🏠</span>
                        <span>Persönlich</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setCategoryFilter('Sonstiges')}
                      className={`rounded-lg ${categoryFilter === 'Sonstiges' ? 'bg-emerald-50 text-emerald-700' : ''}`}
                    >
                      <div className="flex items-center gap-2">
                        <span>📦</span>
                        <span>Sonstiges</span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <ImpulseControl />
                
                <Button
                  onClick={() => setIsAddingItem(true)}
                    className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 relative overflow-hidden group flex-1 sm:flex-none rounded-xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <Plus className="w-4 h-4 sm:mr-2 relative z-10" />
                    <span className="relative z-10 hidden sm:inline">Neues Produkt</span>
                    <span className="relative z-10 sm:hidden">Neu</span>
                </Button>
                  </div>
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            <Tabs value={activeTab} onValueChange={(value: any) => setActiveTab(value)} className="mb-6">
              {/* Mobile: Scrollbare Tab-Liste mit Scroll-Indikatoren */}
              <div className="relative sm:hidden">
                <div className="overflow-x-auto scrollbar-hide">
                  <TabsList className="flex w-max min-w-full bg-gray-50 border border-gray-200 p-1 rounded-xl">
                    <TabsTrigger 
                      value="all" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Alle
                    </TabsTrigger>
                    <TabsTrigger 
                      value="waiting" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Warteliste
                    </TabsTrigger>
                    <TabsTrigger 
                      value="bought" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Gekauft
                    </TabsTrigger>
                    <TabsTrigger 
                      value="skipped" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Vermieden
                    </TabsTrigger>
                    <TabsTrigger 
                      value="subscriptions" 
                      className="px-4 py-2 text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-sm transition-all duration-200 rounded-lg"
                    >
                      Abos
                    </TabsTrigger>
                  </TabsList>
                </div>
                {/* Scroll-Indikatoren */}
                <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-gray-50 to-transparent pointer-events-none rounded-r-xl"></div>
                <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-gray-50 to-transparent pointer-events-none rounded-l-xl"></div>
                {/* Scroll-Hinweis */}
                <div className="flex justify-center mt-2">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span>←</span>
                    <span>Wischen zum Navigieren</span>
                    <span>→</span>
                  </div>
                </div>
              </div>
              
              {/* Desktop: Grid Layout mit verbesserter Hervorhebung */}
              <div className="hidden sm:block">
                <TabsList className="grid w-full grid-cols-5 bg-gray-50 border border-gray-200 p-1 rounded-xl">
                  <TabsTrigger 
                    value="all" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Alle
                  </TabsTrigger>
                  <TabsTrigger 
                    value="waiting" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Warteliste
                  </TabsTrigger>
                  <TabsTrigger 
                    value="bought" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Gekauft
                  </TabsTrigger>
                  <TabsTrigger 
                    value="skipped" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Impulskauf vermieden
                  </TabsTrigger>
                  <TabsTrigger 
                    value="subscriptions" 
                    className="text-sm whitespace-nowrap data-[state=active]:bg-white data-[state=active]:text-emerald-600 data-[state=active]:shadow-md data-[state=active]:font-semibold transition-all duration-200 hover:bg-white/80 rounded-lg"
                  >
                    Abos
                  </TabsTrigger>
                </TabsList>
              </div>
            </Tabs>

            {activeTab === 'subscriptions' ? (
              <SubscriptionOverview />
            ) : (
              <div className="space-y-4">
                {filteredItems.map((item, index) => (
                <div 
                  key={item.id} 
                  className="group animate-in fade-in-0 slide-in-from-bottom-4"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div 
                    className={`relative p-6 border rounded-xl transition-all duration-500 hover:shadow-lg hover:-translate-y-1 cursor-pointer ${
                      animatingItemId === item.id 
                        ? item.status === 'bought' 
                          ? 'translate-x-full opacity-0 bg-green-50/80' 
                          : item.status === 'skipped' 
                            ? 'translate-x-full opacity-0 bg-red-50/80'
                            : ''
                        : ''
                    } ${
                      item.status === 'bought' ? 'bg-gradient-to-br from-blue-50/60 to-indigo-50/60 border-blue-200 shadow-blue-100/20' :
                      'bg-white border-gray-200 hover:border-emerald-300 shadow-gray-100/30'
                    }`}
                    onClick={(e) => {
                      // Verhindere Toggle wenn auf interaktive Elemente geklickt wird
                      if (e.target instanceof Element && (
                        e.target.closest('button') || 
                        e.target.closest('a') || 
                        e.target.closest('input') ||
                        e.target.closest('textarea') ||
                        e.target.closest('[role="menuitem"]') ||
                        e.target.closest('[role="button"]') ||
                        e.target.closest('.recharts-wrapper') ||
                        e.target.tagName === 'INPUT' ||
                        e.target.tagName === 'TEXTAREA' ||
                        e.target.tagName === 'BUTTON'
                      )) {
                        return;
                      }
                      setExpandedProduct(expandedProduct === item.id ? null : item.id);
                    }}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                      <div className="flex-grow min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <h4 className="font-medium text-lg text-gray-900 truncate group-hover:text-emerald-700 transition-colors duration-300">
                            {item.name}
                          </h4>
                          <ProductEditMenu
                            productId={item.id}
                            currentName={item.name}
                            currentUrl={item.url || ''}
                            currentPrice={item.price}
                            currentCategory={item.category}
                            onUpdate={fetchWaitlistItems}
                            onLocalUpdate={updateLocalProduct}
                          />
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 mb-4">
                          <Badge 
                            variant="outline" 
                            className={`${getCategoryColor(item.category)} border bg-white transition-all duration-200 hover:scale-105 rounded-lg`}
                            title={getCategoryInfo(item.category).description}
                          >
                            <span className="mr-1">{getCategoryInfo(item.category).icon}</span>
                            {item.category}
                          </Badge>
                          <span className="text-xl font-light text-gray-900">
                            {Math.round(item.price)}€
                          </span>
                          {item.status !== 'waiting' && (
                            <Badge 
                              variant={item.status === 'bought' ? 'default' : 'secondary'}
                              className={`${
                                item.status === 'bought' 
                                  ? 'bg-blue-500 hover:bg-blue-600 border-0' 
                                  : 'bg-emerald-500 hover:bg-emerald-600 text-white border-0'
                              } transition-colors duration-300 rounded-lg`}
                            >
                              {item.status === 'bought' ? '✓ Gekauft' : '🎉 Impulskauf vermieden'}
                            </Badge>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row items-start gap-3 flex-shrink-0">
                        {/* Progress Bar Container - responsive Breite */}
                        {item.status === 'waiting' && (
                          <div className="w-full sm:w-48">
                            <div className="flex items-center gap-2 mb-3">
                              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Clock className="w-4 h-4 text-amber-600" />
                              </div>
                              <div className="min-w-0">
                                <span className="text-lg font-medium text-amber-600 block">
                                  {getDaysLeft(item.created_at)} Tage
                                </span>
                                <p className="text-xs text-gray-500 leading-tight">verbleibend</p>
                              </div>
                            </div>
                            <div className="w-full">
                              <div className="flex justify-between text-xs text-gray-500 mb-2">
                                <span>Start</span>
                                <span>30 Tage</span>
                              </div>
                              <div className="w-full bg-gray-200 rounded-full h-2 border border-gray-300 overflow-hidden">
                                <div 
                                  className={`h-2 rounded-full transition-all duration-300 ${
                                    getDaysLeft(item.created_at) <= 0 ? 'bg-gradient-to-r from-emerald-500 to-green-500' :
                                    getDaysLeft(item.created_at) <= 7 ? 'bg-gradient-to-r from-red-400 to-red-500' :
                                    getDaysLeft(item.created_at) <= 14 ? 'bg-gradient-to-r from-orange-400 to-orange-500' :
                                    'bg-gradient-to-r from-emerald-400 to-emerald-500'
                                  }`}
                                  style={{ width: `${Math.min(100, ((30 - getDaysLeft(item.created_at)) / 30) * 100)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Action Buttons Container - feste Breite für konsistentes Alignment */}
                        <div className="flex items-center gap-2 justify-end w-full sm:w-24 sm:flex-shrink-0">
                          {/* Link Button - fester Platz reserviert */}
                          <div className="w-10 h-10 flex items-center justify-center">
                            {item.url ? (
                            <a 
                              href={item.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="w-8 h-8 flex items-center justify-center text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-all duration-300 hover:scale-110 border border-blue-200"
                              title="Produkt-Link öffnen"
                            >
                              <Link className="w-4 h-4" />
                            </a>
                            ) : (
                              <div className="w-8 h-8"></div>
                          )}
                          </div>
                          
                          {/* Menu Button - fester Platz */}
                          <div className="w-10 h-10 flex items-center justify-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 w-8 p-0 hover:bg-gray-100 rounded-lg transition-all duration-300 hover:scale-110 border border-gray-200"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48 bg-white border-gray-200 rounded-xl shadow-lg">
                              {item.status !== 'waiting' && (
                                <DropdownMenuItem 
                                  onClick={() => handleStatusChange(item.id, 'waiting')}
                                  className="flex items-center gap-2 rounded-lg"
                                >
                                  <Clock className="w-4 h-4" />
                                  Zurück zur Warteliste
                                </DropdownMenuItem>
                              )}
                              {item.status === 'waiting' && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleStatusChange(item.id, 'bought')}
                                    className="flex items-center gap-2 text-blue-600 rounded-lg"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                    Als gekauft markieren
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleStatusChange(item.id, 'skipped')}
                                    className="flex items-center gap-2 text-emerald-600 rounded-lg"
                                  >
                                    <XCircle className="w-4 h-4" />
                                    Impulskauf vermieden
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <ProductDetails
                      productId={item.id}
                      expanded={expandedProduct === item.id}
                      onToggle={() => setExpandedProduct(expandedProduct === item.id ? null : item.id)}
                      onPriceUpdate={fetchWaitlistItems}
                    />
                  </div>
                </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      <AddProductDialog
        isOpen={isAddingItem}
        onClose={() => setIsAddingItem(false)}
        onProductAdded={fetchWaitlistItems}
      />

      <ProfileModal 
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
      />

      {/* Upgrade Prompt for Free Users */}
      {showUpgradePrompt && (subscription?.tier === 'free' || !subscription) && (
        <UpgradePrompt
          onClose={() => {
            setShowUpgradePrompt(false);
            localStorage.setItem('upgradePromptLastDismissed', Date.now().toString());
          }}
          onUpgrade={() => {
            setShowUpgradePrompt(false);
            localStorage.setItem('upgradePromptLastDismissed', Date.now().toString());
            showUpgradeModal();
          }}
        />
      )}

      {/* Welcome Overlay for New Users */}
      <WelcomeOverlay />
    </div>
  );
};

export default Dashboard;
